import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  alpha,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { isMSP } from "../../helpers/util_helper";
import { getInvoiceDownload, getInvoicePreview, generateInvoice, postInvoiceAction } from "../../helpers/auth_aws_helper";
import {
  getCustomerInvoices,
  getCustomerBillingMetrics,
  getInvoiceRuleSnapshots,
} from "../../store/billingManagement/billingManagementSlice";
import CustomerInvoicesHeader from "./CustomerInvoicesHeader";
import CustomerInvoicesMetrics, { formatCurrency } from "./CustomerInvoicesMetrics";

// API sends datetimes in UTC; parse as UTC (append Z if no timezone) so toLocale* shows browser local time
const parseUtc = (dateString) => {
  const s = String(dateString).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const isIsoDatetime = /^\d{4}-\d{2}-\d{2}T\d/.test(s);
  const hasTimezone = s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s);
  const asUtc = isIsoDatetime && !hasTimezone ? `${s.replace(/\.\d+$/, "")}Z` : s;
  const date = new Date(asUtc);
  return isNaN(date.getTime()) ? null : date;
};

const formatDate = (dateString) => {
  const date = parseUtc(dateString);
  if (!date) return "—";
  try {
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const formatDateTime = (dateString) => {
  const date = parseUtc(dateString);
  if (!date) return "—";
  try {
    return date.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

// Short invoice ref for table display (e.g. INV-1069c from invoice_id), same as index.js
const getShortInvoiceRef = (invoiceId) => {
  if (!invoiceId) return null;
  const clean = String(invoiceId).replace(/-/g, "");
  return clean.length >= 5 ? `INV-${clean.slice(-5)}` : `INV-${clean}`;
};

// Sum amount from metrics.by_rule_category for an invoice
const getInvoiceAmount = (inv) => {
  const m = inv.metrics?.by_rule_category;
  if (!m || typeof m !== "object") return null;
  const sum = Object.values(m).reduce((a, b) => a + (Number(b) || 0), 0);
  return sum;
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Year options for filter: current year down to 2000
const YEAR_OPTIONS = Array.from({ length: new Date().getFullYear() + 1 - 2000 }, (_, i) => 2000 + i).reverse();

const COLUMN_STORAGE_KEY = "customerInvoices_columnVisibility";

// Column definitions: id, label, defaultVisible (new optional columns hidden by default)
const TABLE_COLUMNS = [
  { id: "billing_period", label: "Billing period", defaultVisible: true },
  { id: "amount", label: "Amount", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "run_type", label: "Run Type", defaultVisible: true },
  { id: "requested_at", label: "Requested at", defaultVisible: true },
  { id: "requested_by", label: "Requested by", defaultVisible: false },
  { id: "finalized_at", label: "Finalized at", defaultVisible: true },
  { id: "approved_at", label: "Approved at", defaultVisible: false },
  { id: "approved_by", label: "Approved by", defaultVisible: false },
  { id: "sent_at", label: "Sent at", defaultVisible: false },
  { id: "sent_by", label: "Sent by", defaultVisible: false },
  { id: "cancelled_at", label: "Cancelled at", defaultVisible: false },
  { id: "cancelled_by", label: "Cancelled by", defaultVisible: false },
  { id: "cancel_reason", label: "Cancel reason", defaultVisible: false },
  { id: "applied_rules", label: "Applied Rules", defaultVisible: true },
  { id: "actions", label: "Actions", defaultVisible: true },
];

const getDefaultColumnVisibility = () =>
  TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: col.defaultVisible }), {});

const loadColumnVisibility = () => {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const defaults = getDefaultColumnVisibility();
      return { ...defaults, ...parsed };
    }
  } catch (_) {}
  return getDefaultColumnVisibility();
};

const saveColumnVisibility = (visibility) => {
  try {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibility));
  } catch (_) {}
};

// Format billing period as "Month YYYY" (e.g. November 2025) from date string
const formatMonthYear = (dateString) => {
  if (!dateString) return "—";
  try {
    const s = String(dateString);
    const match = s.match(/^(\d{4})-(\d{2})/);
    if (match) {
      const [, y, m] = match;
      const monthNum = parseInt(m, 10);
      return `${MONTH_NAMES[monthNum - 1]} ${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return "—";
  }
};

const CustomerInvoices = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { customerOrgCode } = useParams();

  const customerName = location.state?.customerName || "Customer";

  const isMSPUser = isMSP();
  const {
    invoices,
    isFetchingInvoices,
    fetchInvoicesError,
    invoicesPagination,
    customerMetrics,
    isFetchingCustomerMetrics,
    customerMetricsError,
    invoiceRuleSnapshots,
    isFetchingInvoiceRuleSnapshots,
    fetchInvoiceRuleSnapshotsError,
  } = useSelector((state) => state.billingManagement);

  const [yearFilter, setYearFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [previewingInvoiceId, setPreviewingInvoiceId] = useState(null);
  const [downloadSnackbar, setDownloadSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(loadColumnVisibility);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const columnsMenuOpen = Boolean(columnsAnchorEl);
  // Generate invoice dialog (period from row; confirmation only)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatePeriod, setGeneratePeriod] = useState(null); // { year, month, label }
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateNoRules, setGenerateNoRules] = useState(false);
  const [generateNoRulesMessage, setGenerateNoRulesMessage] = useState("");
  // Invoice action dialog (opened from Status column button)
  const [actionInvoice, setActionInvoice] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Download invoice: get pre-signed URL and open in new tab
  const handleDownloadInvoice = async (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setDownloadingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoiceDownload(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Download started.", severity: "success" });
      } else {
        const data = response?.data ?? response;
        const msg = data?.detail ?? data?.error ?? data?.message ?? response?.message ?? "Download not available for this invoice.";
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data?.detail ?? data?.error ?? data?.message ?? error?.message ?? "Download failed.";
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // Preview invoice: get pre-signed URL and open in new tab
  const handlePreviewInvoice = async (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setPreviewingInvoiceId(invoiceId);
    setDownloadSnackbar((prev) => ({ ...prev, open: false }));
    try {
      const response = await getInvoicePreview(customerOrgCode, invoiceId);
      if (response?.status === 200 && response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        setDownloadSnackbar({ open: true, message: "Preview opened in new tab.", severity: "success" });
      } else {
        const data = response?.data ?? response;
        const msg = data?.detail ?? data?.error ?? data?.message ?? response?.message ?? "Preview not available for this invoice.";
        setDownloadSnackbar({ open: true, message: String(msg), severity: "error" });
      }
    } catch (error) {
      const data = error?.response?.data ?? error?.data;
      const message = data?.detail ?? data?.error ?? data?.message ?? error?.message ?? "Preview failed.";
      setDownloadSnackbar({ open: true, message: String(message), severity: "error" });
    } finally {
      setPreviewingInvoiceId(null);
    }
  };

  const handleOpenRulesModal = (invoiceId) => {
    if (!customerOrgCode || !invoiceId) return;
    setSelectedInvoiceId(invoiceId);
    setRulesModalOpen(true);
    dispatch(getInvoiceRuleSnapshots({ customerOrgCode, invoiceId }));
  };

  const handleCloseRulesModal = () => {
    setRulesModalOpen(false);
    setSelectedInvoiceId(null);
  };

  // Parse billing period from invoice row (e.g. "2026-02-01" -> { year: 2026, month: 2 })
  const getPeriodFromInvoice = (inv) => {
    const start = inv?.billing_period_start || inv?.billing_period_end;
    if (!start || typeof start !== "string") return null;
    const match = String(start).match(/^(\d{4})-(\d{2})/);
    if (!match) return null;
    const [, y, m] = match;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    if (month < 1 || month > 12) return null;
    const label = formatMonthYear(start);
    return { year, month, label };
  };

  const handleOpenGenerateDialog = (inv) => {
    const period = getPeriodFromInvoice(inv);
    if (!period) return;
    setGeneratePeriod(period);
    setGenerateNoRules(false);
    setGenerateNoRulesMessage("");
    setGenerateError(null);
    setGenerateDialogOpen(true);
  };

  const handleCloseGenerateDialog = () => {
    setGenerateDialogOpen(false);
    setGeneratePeriod(null);
    setGenerateNoRules(false);
    setGenerateError(null);
    setGenerateLoading(false);
  };

  const handleGenerateInvoice = async (overrideWarnings = false) => {
    if (!customerOrgCode || !generatePeriod) return;
    const { year, month } = generatePeriod;
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      const response = await generateInvoice(customerOrgCode, {
        year,
        month,
        override_warnings: overrideWarnings,
      });
      const data = response?.data ?? response;
      const message = data?.message ?? response?.message ?? "";
      const status = response?.status;
      if (status === 200 || (data && (status == null || status === 200))) {
        const msgLower = String(message).toLowerCase();
        if (!overrideWarnings && (msgLower.includes("no rules") || msgLower.includes("no rules found"))) {
          setGenerateNoRules(true);
          setGenerateNoRulesMessage(message || "No rules found for the selected date range.");
        } else {
          setDownloadSnackbar({ open: true, message: message || "Invoice generation request accepted.", severity: "success" });
          handleCloseGenerateDialog();
          dispatch(getCustomerInvoices({ customerOrgCode, page, page_size: pageSize, ...(yearFilter ? { year: yearFilter } : {}) }));
          if (customerOrgCode) {
            dispatch(getCustomerBillingMetrics({ customerOrgCode, ...(yearFilter ? { year: yearFilter } : {}) }));
          }
        }
      } else {
        setGenerateError(data?.detail ?? data?.error ?? data?.message ?? message ?? "Request failed.");
      }
    } catch (error) {
      const errData = error?.response?.data ?? error?.data;
      setGenerateError(errData?.detail ?? errData?.error ?? errData?.message ?? error?.message ?? "Request failed.");
    } finally {
      setGenerateLoading(false);
    }
  };

  const INVOICE_ACTIONS = [
    { id: "APPROVE", label: "Approve" },
    { id: "UNAPPROVE", label: "Unapprove" },
    { id: "SEND", label: "Send" },
    { id: "RETRY", label: "Retry delivery" },
    { id: "CANCEL", label: "Cancel invoice" },
  ];

  // Preconditions: which action(s) to show as buttons in Status column (APPROVED can show both Send and Unapprove)
  const getActionsForInvoice = (inv) => {
    const ui = (inv?.ui_status || inv?.status || "").toUpperCase();
    if (ui === "PENDING_APPROVAL") return [{ action: "APPROVE", label: "Approve" }];
    if (ui === "APPROVED") return [{ action: "SEND", label: "Send" }, { action: "UNAPPROVE", label: "Unapprove" }];
    if (ui === "DELIVERY_FAILED") return [{ action: "RETRY", label: "Retry" }];
    if (ui === "SENT") return [{ action: "CANCEL", label: "Cancel" }];
    return [];
  };

  const handleOpenActionDialog = (inv, actionType) => {
    setActionInvoice(inv);
    setActionType(actionType);
    setCancelReason("");
    setActionError(null);
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setActionType(null);
    setActionInvoice(null);
    setCancelReason("");
    setActionError(null);
    setActionLoading(false);
  };

  const handleConfirmInvoiceAction = async () => {
    if (!customerOrgCode || !actionInvoice?.invoice_id || !actionType) return;
    const payload = actionType === "CANCEL" ? { action: "CANCEL", cancel_reason: cancelReason || null } : { action: actionType, cancel_reason: null };
    setActionLoading(true);
    setActionError(null);
    try {
      const response = await postInvoiceAction(customerOrgCode, actionInvoice.invoice_id, payload);
      const data = response?.data ?? response;
      const message = data?.message ?? response?.message ?? "";
      const status = response?.status;
      if (status === 200 || (data && (status == null || status === 200))) {
        setDownloadSnackbar({ open: true, message: message || "Action completed.", severity: "success" });
        handleCloseActionDialog();
        dispatch(getCustomerInvoices({ customerOrgCode, page, page_size: pageSize, ...(yearFilter ? { year: yearFilter } : {}) }));
        if (customerOrgCode) dispatch(getCustomerBillingMetrics({ customerOrgCode, ...(yearFilter ? { year: yearFilter } : {}) }));
      } else {
        setActionError(data?.detail ?? data?.error ?? data?.message ?? message ?? "Action failed.");
      }
    } catch (error) {
      const errData = error?.response?.data ?? error?.data;
      setActionError(errData?.detail ?? errData?.error ?? errData?.message ?? error?.message ?? "Action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleColumnVisibilityChange = (columnId) => (e) => {
    const next = { ...columnVisibility, [columnId]: e.target.checked };
    setColumnVisibility(next);
    saveColumnVisibility(next);
  };

  const visibleColumns = TABLE_COLUMNS.filter((col) => columnVisibility[col.id]);
  const colSpanForEmpty = visibleColumns.length;

  const stickyHeaderCell = (colId, isFirst, isLast) => {
    const base = { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" };
    if (isFirst) return { ...base, position: "sticky", left: 0, zIndex: 2, minWidth: 140, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" };
    if (isLast) return { ...base, position: "sticky", right: 0, zIndex: 2, minWidth: 160, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)" };
    return base;
  };

  const stickyBodyCell = (colId, isFirst, isLast) => {
    const base = { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem", backgroundColor: "#fff" };
    if (isFirst) return { ...base, position: "sticky", left: 0, zIndex: 1, minWidth: 140, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" };
    if (isLast) return { ...base, position: "sticky", right: 0, zIndex: 1, minWidth: 160, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)" };
    return base;
  };

  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
  }, [isMSPUser, history]);

  useEffect(() => {
    if (customerOrgCode && isMSPUser) {
      const params = { page, page_size: pageSize };
      if (yearFilter) params.year = yearFilter;
      dispatch(getCustomerInvoices({ customerOrgCode, ...params }));
    }
  }, [customerOrgCode, isMSPUser, dispatch, page, pageSize, yearFilter]);

  // Fetch customer-scope billing metrics (API: scope=customer, customer_orgcode required)
  useEffect(() => {
    if (customerOrgCode && isMSPUser) {
      const payload = { customerOrgCode };
      if (yearFilter) payload.year = yearFilter;
      dispatch(getCustomerBillingMetrics(payload));
    }
  }, [customerOrgCode, isMSPUser, dispatch, yearFilter]);

  const filteredInvoices = (invoices || []).filter((inv) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (inv.invoice_id && inv.invoice_id.toLowerCase().includes(term)) ||
      (inv.billing_period_start && inv.billing_period_start.includes(term)) ||
      (inv.billing_period_end && inv.billing_period_end.includes(term))
    );
  });

  // Metric cards use API customer-scope metrics when available; fallback to list-derived values only for table context
  const totalInvoices = customerMetrics?.total_invoices ?? invoicesPagination?.total ?? 0;
  const totalBilled = customerMetrics?.total_billed ?? (invoices || []).reduce((sum, inv) => sum + (getInvoiceAmount(inv) || 0), 0);
  const latestInvoiceAmount = customerMetrics?.latest_invoice_amount ?? (invoices?.[0] ? getInvoiceAmount(invoices[0]) : null);

  if (!customerOrgCode) {
    history.replace("/billing-management");
    return null;
  }

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <CustomerInvoicesHeader customerName={customerName} />

      <CustomerInvoicesMetrics
        customerName={customerName}
        totalInvoices={totalInvoices}
        totalBilled={totalBilled}
        latestInvoiceAmount={latestInvoiceAmount}
        lastInvoicePeriodStart={invoices?.[0]?.billing_period_start}
        customerMetrics={customerMetrics}
        isFetchingCustomerMetrics={isFetchingCustomerMetrics}
        customerMetricsError={customerMetricsError}
      />

      {/* Table card: filters in header + table */}
      <Card sx={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "none", backgroundColor: "#ffffff", overflow: "hidden" }}>
        {/* Table header: Year dropdown + Search */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, p: 2, borderBottom: "1px solid #e5e7eb", backgroundColor: "#fafafa" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography component="span" sx={{ color: "#64748b", fontSize: "0.875rem", mr: 1.5 }}>
              Month view
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={yearFilter || ""}
                onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : "")}
                displayEmpty
                renderValue={(v) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <CalendarMonthIcon sx={{ color: "#64748b", fontSize: 20 }} />
                    <span>{v ? `Year: ${v}` : "All years"}</span>
                  </Box>
                )}
                sx={{
                  height: 40,
                  backgroundColor: "#ffffff",
                  borderRadius: 1,
                  "& .MuiSelect-select": { py: 1.25, pl: 1.5 },
                }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 320, mt: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } },
                }}
              >
                <MenuItem value="">
                  <em>All years</em>
                </MenuItem>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search by month or invoice ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewColumnIcon />}
              onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
              sx={{
                height: 40,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                borderColor: "#e5e7eb",
                color: "#52525b",
                textTransform: "none",
                "&:hover": { borderColor: "#94a3b8", backgroundColor: "#fafafa" },
              }}
            >
              Columns
            </Button>
          </Box>
          <Popover
            open={columnsMenuOpen}
            anchorEl={columnsAnchorEl}
            onClose={() => setColumnsAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { mt: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", minWidth: 220, p: 1.5 } }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#52525b", mb: 1.5, px: 0.5 }}>
              Show / hide columns
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const next = TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
                  setColumnVisibility(next);
                  saveColumnVisibility(next);
                }}
                sx={{ textTransform: "none", fontSize: "0.8125rem", color: "#3b82f6", minWidth: 0, px: 1 }}
              >
                Show all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const next = TABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: false }), {});
                  setColumnVisibility(next);
                  saveColumnVisibility(next);
                }}
                sx={{ textTransform: "none", fontSize: "0.8125rem", color: "#64748b", minWidth: 0, px: 1 }}
              >
                Hide all
              </Button>
            </Box>
            <FormGroup>
              {TABLE_COLUMNS.map((col) => (
                <FormControlLabel
                  key={col.id}
                  control={
                    <Checkbox
                      checked={!!columnVisibility[col.id]}
                      onChange={handleColumnVisibilityChange(col.id)}
                      size="small"
                      sx={{ color: "#71717a", "&.Mui-checked": { color: "#3b82f6" } }}
                    />
                  }
                  label={col.label}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem", color: "#374151" } }}
                />
              ))}
            </FormGroup>
          </Popover>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="medium" stickyHeader sx={{ minWidth: 720, fontSize: "0.875rem" }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col, index) => (
                  <TableCell
                    key={col.id}
                    align={["actions", "applied_rules", "status", "run_type"].includes(col.id) ? "center" : "left"}
                    sx={stickyHeaderCell(col.id, index === 0, index === visibleColumns.length - 1)}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetchingInvoices ? (
                <TableRow>
                  <TableCell colSpan={colSpanForEmpty} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
                  </TableCell>
                </TableRow>
              ) : fetchInvoicesError ? (
                <TableRow>
                  <TableCell colSpan={colSpanForEmpty} align="center" sx={{ py: 4, color: "#ef4444", fontSize: "0.875rem" }}>
                    {typeof fetchInvoicesError === "string"
                      ? fetchInvoicesError
                      : (fetchInvoicesError?.detail || fetchInvoicesError?.error || fetchInvoicesError?.message || "Failed to load invoices.")}
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpanForEmpty} align="center" sx={{ py: 6, color: "#71717a", fontSize: "0.875rem" }}>
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((inv) => {
                  const monthLabel = formatMonthYear(inv.billing_period_start || inv.billing_period_end);
                  const uiStatus = inv.ui_status || inv.status || "—";
                  const renderCell = (colId, colIndex) => {
                    const sticky = stickyBodyCell(colId, colIndex === 0, colIndex === visibleColumns.length - 1);
                    switch (colId) {
                      case "billing_period":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#52525b" }}>{monthLabel}</TableCell>;
                      case "amount":
                        return (
                          <TableCell key={colId} sx={sticky} align="left">
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.875rem" }}>
                                {formatCurrency(inv.total_calculated_amount)}
                              </Typography>
                              {getShortInvoiceRef(inv.invoice_id) && (
                                <Typography sx={{ display: "block", fontSize: "0.75rem", color: "#94a3b8", fontFamily: "monospace", mt: 0.25 }}>
                                  {getShortInvoiceRef(inv.invoice_id)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        );
                      case "status": {
                        const actions = getActionsForInvoice(inv);
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            {actions.length > 0 ? (
                              <Tooltip title={`Status: ${uiStatus}`}>
                                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5 }}>
                                  {actions.map(({ action: act, label }) => (
                                    <Button
                                      key={act}
                                      size="small"
                                      variant={act === "UNAPPROVE" ? "outlined" : "contained"}
                                      disabled={actionLoading || !inv.invoice_id}
                                      onClick={() => handleOpenActionDialog(inv, act)}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.8125rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        boxShadow: "none",
                                        ...(act === "CANCEL"
                                          ? {
                                              backgroundColor: "#64748b",
                                              color: "#fff",
                                              "&:hover": { backgroundColor: "#475569", boxShadow: "none" },
                                            }
                                          : act === "UNAPPROVE"
                                            ? {
                                                borderColor: "#94a3b8",
                                                color: "#64748b",
                                                "&:hover": { borderColor: "#64748b", backgroundColor: "rgba(100,116,139,0.08)" },
                                              }
                                            : {
                                                backgroundColor: "#3b82f6",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#2563eb", boxShadow: "none" },
                                              }),
                                      }}
                                    >
                                      {label}
                                    </Button>
                                  ))}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  color:
                                    uiStatus === "CANCELLED" || uiStatus === "FAILED"
                                      ? "#64748b"
                                      : uiStatus === "FINALIZED" || uiStatus === "APPROVED" || uiStatus === "SENT" || uiStatus === "SENDING"
                                        ? "#10b981"
                                        : uiStatus === "DELIVERY_FAILED"
                                          ? "#ef4444"
                                          : "#3b82f6",
                                }}
                              >
                                {uiStatus}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      }
                      case "run_type":
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            {(() => {
                              const runType = (inv.generation_type || "").toUpperCase();
                              if (runType === "ON_DEMAND") {
                                return (
                                  <Tooltip title="On demand">
                                    <TouchAppIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                                  </Tooltip>
                                );
                              }
                              if (runType === "RECURRING" || runType === "SCHEDULED") {
                                return (
                                  <Tooltip title={runType === "SCHEDULED" ? "Scheduled" : "Recurring"}>
                                    <ScheduleIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                                  </Tooltip>
                                );
                              }
                              return <Typography sx={{ color: "#52525b", fontSize: "0.875rem" }}>{inv.generation_type || "—"}</Typography>;
                            })()}
                          </TableCell>
                        );
                      case "requested_at":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{formatDateTime(inv.requested_at)}</TableCell>;
                      case "requested_by":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{inv.requested_by ?? "—"}</TableCell>;
                      case "finalized_at":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{formatDateTime(inv.finalized_at)}</TableCell>;
                      case "approved_at":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{formatDateTime(inv.approved_at)}</TableCell>;
                      case "approved_by":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{inv.approved_by ?? "—"}</TableCell>;
                      case "sent_at":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{formatDateTime(inv.sent_at)}</TableCell>;
                      case "sent_by":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{inv.sent_by ?? "—"}</TableCell>;
                      case "cancelled_at":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{formatDateTime(inv.cancelled_at)}</TableCell>;
                      case "cancelled_by":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{inv.cancelled_by ?? "—"}</TableCell>;
                      case "cancel_reason":
                        return <TableCell key={colId} sx={{ ...sticky, color: "#71717a" }}>{inv.cancel_reason ?? "—"}</TableCell>;
                      case "applied_rules":
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            <Tooltip title="View rule snapshots used for this invoice">
                              <Chip
                                icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                                label={inv.rule_application_count != null ? inv.rule_application_count : "0"}
                                size="small"
                                onClick={() => handleOpenRulesModal(inv.invoice_id)}
                                sx={{
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  fontSize: "0.8125rem",
                                  borderRadius: "6px",
                                  backgroundColor: "#f1f5f9",
                                  color: "#475569",
                                  "&:hover": { backgroundColor: "#e2e8f0", color: "#334155" },
                                  "& .MuiChip-icon": { color: "inherit" },
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                        );
                      case "actions":
                        return (
                          <TableCell key={colId} align="center" sx={sticky}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.25, flexWrap: "nowrap" }}>
                              <Tooltip title="Generate invoice for this period">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={generateLoading}
                                    onClick={() => handleOpenGenerateDialog(inv)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    <PlayArrowIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={inv.status === "FINALIZED" ? "Preview invoice (opens in new tab)" : "Preview available only for finalized invoices"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={inv.status !== "FINALIZED" || !inv.invoice_id || previewingInvoiceId === inv.invoice_id}
                                    onClick={() => handlePreviewInvoice(inv.invoice_id)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    {previewingInvoiceId === inv.invoice_id ? (
                                      <CircularProgress size={20} sx={{ color: "#71717a" }} />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Download invoice">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!inv.invoice_id || downloadingInvoiceId === inv.invoice_id}
                                    onClick={() => handleDownloadInvoice(inv.invoice_id)}
                                    sx={{ color: "#71717a", "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" } }}
                                  >
                                    {downloadingInvoiceId === inv.invoice_id ? (
                                      <CircularProgress size={20} sx={{ color: "#71717a" }} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        );
                      default:
                        return <TableCell key={colId} sx={sticky}>—</TableCell>;
                    }
                  };
                  return (
                    <TableRow
                      key={inv.invoice_id}
                      sx={{
                        "& td": { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem" },
                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      {visibleColumns.map((col, colIndex) => renderCell(col.id, colIndex))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {actionType ? INVOICE_ACTIONS.find((a) => a.id === actionType)?.label ?? actionType : ""}
        </DialogTitle>
        <DialogContent>
          {actionType === "CANCEL" ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 1.5 }}>
                Cancel reason (optional)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Reason for cancellation"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                multiline
                rows={2}
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
              />
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b", pt: 0.5 }}>
              Are you sure you want to {actionType?.toLowerCase()} this invoice?
            </Typography>
          )}
          {actionError && (
            <Alert severity="error" sx={{ fontSize: "0.8125rem", mt: 1.5 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={handleCloseActionDialog} disabled={actionLoading} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={actionLoading}
            onClick={handleConfirmInvoiceAction}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={generateDialogOpen}
        onClose={handleCloseGenerateDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {generateNoRules ? "No rules for this period" : "Generate invoice"}
        </DialogTitle>
        <DialogContent>
          {generateNoRules ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.875rem", mb: 2 }}>
                {generateNoRulesMessage}
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.8125rem", mb: 2 }}>
                You can still generate an invoice for this period. Do you want to continue?
              </Typography>
            </Box>
          ) : generatePeriod ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                Billing will be generated for period: <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>{generatePeriod.label}</Box>
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 1 }}>
                Do you want to continue?
              </Typography>
              {generateError && (
                <Alert severity="error" sx={{ fontSize: "0.8125rem", mt: 1.5 }} onClose={() => setGenerateError(null)}>
                  {generateError}
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          {generateNoRules ? (
            <>
              <Button type="button" onClick={handleCloseGenerateDialog} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={generateLoading}
                onClick={() => handleGenerateInvoice(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {generateLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Generate anyway"}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" onClick={handleCloseGenerateDialog} disabled={generateLoading} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={generateLoading}
                onClick={() => handleGenerateInvoice(false)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {generateLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Generate"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={rulesModalOpen}
        onClose={handleCloseRulesModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem", borderBottom: "1px solid #e4e4e7", pb: 1.5 }}>
          Invoice Rule Snapshots
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 2.5, minHeight: 120 }}>
          {isFetchingInvoiceRuleSnapshots ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
            </Box>
          ) : fetchInvoiceRuleSnapshotsError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {typeof fetchInvoiceRuleSnapshotsError === "string"
                ? fetchInvoiceRuleSnapshotsError
                : fetchInvoiceRuleSnapshotsError?.detail || fetchInvoiceRuleSnapshotsError?.message || "Failed to load rule snapshots."}
            </Alert>
          ) : invoiceRuleSnapshots?.items?.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
              {invoiceRuleSnapshots.items.map((item, idx) => {
                const snap = item.rule_snapshot || {};
                const ruleType = snap.type || snap.rule_type || "";
                const params = snap.parameters && typeof snap.parameters === "object" ? snap.parameters : {};
                const scopes = Array.isArray(snap.scopes) ? snap.scopes : [];
                const hasScopes = scopes.length > 0;
                const scopeByType = scopes.reduce((acc, s) => {
                  const t = s.scopeType || s.scope_type || "OTHER";
                  if (!acc[t]) acc[t] = [];
                  acc[t].push(s);
                  return acc;
                }, {});
                const scopeTypeLabel = (t) => {
                  if (t === "SERVICE") return "AWS Services";
                  if (t === "REGION") return "AWS Regions";
                  if (t === "LINKED_ACCOUNT") return "Linked Accounts";
                  return t.replace(/_/g, " ");
                };
                return (
                  <Accordion
                    key={item.billing_rule_id || idx}
                    disableGutters
                    square
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      "&:before": { display: "none" },
                      "&.Mui-expanded": { mt: 0, mb: 0 },
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": { my: 1.5, alignItems: "center" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0f172a", lineHeight: 1.3 }}>
                            {snap.name || "—"}
                          </Typography>
                          {item.applied_at && (
                            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontWeight: 400 }}>
                              Applied {formatDateTime(item.applied_at)}
                            </Typography>
                          )}
                        </Box>
                        {item.total_impact_amount != null && (
                          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#0f172a", flexShrink: 0 }}>
                            {formatCurrency(item.total_impact_amount)}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
                      {/* Configuration Parameters */}
                      {Object.keys(params).length > 0 && (
                        <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                            Configuration Parameters
                          </Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1.5, lineHeight: 1.5 }}>
                            Parameters used when this rule was applied at invoice generation time.
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {Object.entries(params).map(([key, value]) => {
                              const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                              return (
                                <Box key={key} sx={{ p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600 }}>{label}</Typography>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#3b82f6", fontWeight: 600 }}>
                                    {typeof value === "boolean"
                                      ? (value ? "Enabled" : "Disabled")
                                      : key === "amount"
                                        ? formatCurrency(value)
                                        : String(value)}
                                    {key === "percent" && "%"}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Application Frequency & Active Period */}
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                          Application Frequency
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                            {snap.frequency === "RECURRING"
                              ? "This rule was applied as recurring (each billing cycle)."
                              : "This rule was applied as one-time."}
                          </Typography>
                          {snap.frequency && (
                            <Chip
                              label={snap.frequency === "RECURRING" ? "Recurring" : "One-time"}
                              size="small"
                              sx={{
                                backgroundColor: snap.frequency === "RECURRING" ? alpha("#3b82f6", 0.1) : alpha("#10b981", 0.1),
                                color: snap.frequency === "RECURRING" ? "#3b82f6" : "#10b981",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            />
                          )}
                        </Box>
                        {(snap.startDate || snap.start_date) && (snap.endDate || snap.end_date) && (
                          <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 0.5 }}>Active Period</Typography>
                            <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500 }}>
                              {formatDate(snap.startDate || snap.start_date)} → {formatDate(snap.endDate || snap.end_date)}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Scope Filters */}
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: hasScopes ? "#ffffff" : "#f8fafc" }}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                          Scope Filters
                        </Typography>
                        {hasScopes ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {Object.entries(scopeByType).map(([scopeType, items]) => (
                              <Box key={scopeType} sx={{ p: 1.5, backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#1e293b", fontWeight: 600, mb: 0.75 }}>
                                  {scopeTypeLabel(scopeType)} — {items.some((i) => (i.operator || "").toUpperCase() === "EXCLUDE") ? "Exclude / Include" : "Include"}
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {items.map((s, i) => (
                                    <Chip
                                      key={i}
                                      label={`${(s.operator || "INCLUDE").toLowerCase() === "exclude" ? "Exclude" : "Include"}: ${s.scopeValue || s.scope_value || "—"}`}
                                      size="small"
                                      sx={{ fontSize: "0.75rem", backgroundColor: "#ffffff", color: "#475569", border: "1px solid #e2e8f0", fontWeight: 500 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                            No scope filters. This rule applied to all accounts, services, and regions.
                          </Typography>
                        )}
                      </Box>

                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ py: 3, color: "#71717a", fontSize: "0.875rem", textAlign: "center" }}>
              No rule snapshots for this invoice.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={downloadSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setDownloadSnackbar((prev) => ({ ...prev, open: false }))} severity={downloadSnackbar.severity}>
          {downloadSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerInvoices;
